import React, {useCallback, useMemo, useState} from 'react';
import {isMobile} from 'react-device-detect';
import {ProductBackgroundImage} from 'components/customer/Product/ProductStyled';
import {BenchTableInterface as Bench} from 'components/customer/BTM/helper/useFetchBenchesData';
import {Thickness} from 'components/customer/BTM/Material/Thickness';
import {
    BorderImage,
    Container,
    Detail,
    Info,
    MaterialContainer,
    NameContainer,
    NoShrinkInfoBox,
    Title,
    VariationRequestOverride,
} from 'components/customer/JobDashboard/columns/ProductDetail';
import {MaterialSwatch} from 'components/customer/BTM/Material/MaterialSwatch';
import styled from 'styled-components';
import {parseHtmlString} from 'shared/helpers/HTMLParser';
import {useResizeObserver} from 'shared/helpers/useResizeObserver';
import {
    Label,
    MiniDetail,
} from 'components/customer/JobDashboard/columns/Preview';
import {OverviewProfiles} from 'components/customer/BTM/Overview/OverviewProfiles';
import {getOptions} from 'components/customer/BTM/helper/editHelpers';
import {cloneDeep} from 'lodash';
import {Stack} from 'react-bootstrap';
import {isDeviceSmall} from 'shared/helpers/DeviceSize';

export const BenchtopDetail = ({bench}: {bench: Bench}) => {
    const isSmallDevice = isDeviceSmall();
    const [detail, setDetail] = useState<string>();
    const {paths} = useMemo(() => {
        return getOptions(cloneDeep({options: bench?.options || []}));
    }, [bench]);

    const setDetailOnClick = useCallback(
        (event: React.MouseEvent) => {
            event.stopPropagation();
            if (detail != bench.material.displayName) {
                setDetail(bench.material.displayName);
            } else {
                setDetail('');
            }
        },
        [bench, detail, setDetail]
    );

    const imageContainerStyle = useMemo(() => {
        const style = {overflow: 'hidden'};

        if (isMobile) {
            return {
                height: 'auto',
                maxHeight: '100px',
                minHeight: '75px',
                width: '75px',
                ...style,
            };
        }

        return style;
    }, [isMobile]);

    const [horizontal, setHorizontal] = useState(false);
    const container = useResizeObserver((entries: ResizeObserverEntry[]) => {
        if (entries.length > 0) {
            const {contentRect} = entries[0];
            const {width} = contentRect;

            // NOTE: 393px out of 592px is covered by color and hardware icons
            // which is pretty much static. so we give 199px for variation
            // request. if there is enough space to fit 199px wide variation
            // request box horizontally we set this to true
            setHorizontal(width > 510);
        }
    });

    return (
        <Container $isMobile={isSmallDevice} className="product-detail">
            <BorderImage $isMobile={isSmallDevice}>
                <div className="image-container" style={imageContainerStyle}>
                    <ProductBackgroundImage
                        src={
                            bench.cached_image
                                ? `${bench.cached_image}?buster=${Date.now()}`
                                : bench.shape.image
                        }
                        alt={bench.shape.name}
                        style={{
                            width: `${isMobile ? '75px' : '150px'}`,
                            objectFit: 'cover',
                        }}
                        cachingInProgress={bench.is_cached_image_loading}
                        placeholderImg={`url(${bench.shape.image})`}
                    />
                </div>
            </BorderImage>
            <NameContainer
                $horizontal={horizontal}
                ref={container}
                $verticalGap={10}>
                <div>
                    <Stack
                        direction="horizontal"
                        gap={3}
                        className="align-items-start">
                        <div>
                            <Title $isMobile={isSmallDevice}>
                                {bench.shape.name} Bench
                            </Title>
                            <Info $isMobile={isSmallDevice}>
                                <div>
                                    <ShrinkVertical $isMobile={isSmallDevice}>
                                        <h1>Selections</h1>
                                        <MaterialContainer
                                            $isMobile={isSmallDevice}>
                                            {!isMobile ? (
                                                <section>
                                                    <Label>Thickness</Label>
                                                    <Thickness
                                                        readOnly
                                                        size={
                                                            bench.material
                                                                .thickness
                                                        }
                                                        isSelectedDefault
                                                        imageSize="sm"
                                                    />
                                                </section>
                                            ) : null}
                                            <section>
                                                <Label>Colour</Label>
                                                <MaterialSwatchSmall
                                                    tooltip
                                                    size="xs"
                                                    smallPreview
                                                    setDetail={setDetailOnClick}
                                                    material={{
                                                        ...bench.material,
                                                        image: {
                                                            ...bench?.material
                                                                ?.image,
                                                            name: `/${bench?.material?.image?.image_url}`,
                                                        },
                                                    }}
                                                />
                                                <MiniDetail>
                                                    {isMobile
                                                        ? `${bench.material.thickness}mm `
                                                        : null}
                                                    <em>
                                                        {
                                                            bench.material
                                                                .finish.name
                                                        }
                                                    </em>
                                                </MiniDetail>
                                            </section>
                                            <section>
                                                <Label>Profiles</Label>
                                                <SmallOverviewProfiles
                                                    benchPaths={paths}
                                                    benchMaterial={
                                                        bench.material
                                                    }
                                                    size="sm"
                                                />
                                            </section>
                                        </MaterialContainer>
                                    </ShrinkVertical>
                                </div>
                            </Info>
                        </div>
                    </Stack>
                    <Detail>{parseHtmlString(detail)}</Detail>
                </div>
                {bench.comment.length > 0 && !isMobile ? (
                    <VariationRequestOverride
                        product={bench}
                        $horizontal={horizontal}
                    />
                ) : null}
            </NameContainer>
        </Container>
    );
};

const ShrinkVertical = styled(NoShrinkInfoBox)`
    height: fit-content;
`;

const MaterialSwatchSmall = styled(MaterialSwatch)`
    margin-top: 0;
    margin-left: 5px;
`;

const SmallOverviewProfiles = styled(OverviewProfiles)`
    gap: 10px;

    > div {
        margin: 0;
        text-align: left;
        font-size: 1em;
    }
`;
